import { Fragment, useState, type FormEvent } from 'react';
import {
  useTenantUsers, useGrantableRoles, useInviteTenantUser, useRemoveTenantUser, useSetUserAccess,
  useResendInvite, useRevokeInvite,
  type TenantUser,
} from '../../lib/api/hooks-tms.js';
import { useChangeMembershipRole } from '../../lib/api/hooks-roles.js';
import { RolesSection } from './RolesSection.js';
import { AccessPicker, accessSummary, type Access } from './AccessPicker.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { toastError } from '../../components/ui/Toast.js';
import {
  TableSkeleton, Empty, ErrorBanner, Badge, SectionHead, SearchInput,
} from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { useSession } from '../../lib/auth/session.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Portal users for this account.
 *
 * A platform operator manages these for any customer; a tenant admin manages
 * them for their own PBX only. Same screen and same endpoints either way —
 * scope is enforced server-side, so there is no second implementation to keep
 * in step (ADR-019).
 */
export function UsersTab({
  tenantId,
  title = 'Portal users',
  subtitle = 'People who can sign in and manage this PBX.',
}: {
  tenantId: string;
  /** The admin account view calls these "TMS users": the customer's portal is the TMS. */
  title?: string;
  subtitle?: string;
}) {
  const can = useSession((s) => s.can);
  const users = useTenantUsers(tenantId);
  const remove = useRemoveTenantUser(tenantId);
  const resend = useResendInvite(tenantId);
  const revoke = useRevokeInvite(tenantId);
  const [resent, setResent] = useState<{ email: string; link: string; emailed: boolean } | null>(null);
  const qc = useQueryClient();
  const step = useStepUp();
  // For a lost phone with no recovery codes left: audited, and the admin confirms it is them.
  const resetMfa = useMutation({
    mutationFn: (userId: string) => api.delete<void>(`/v1/users/${userId}/mfa`),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['tenant-users', tenantId] }); },
  });
  const me = useSession((s) => s.session?.user.id);
  const [inviting, setInviting] = useState(false);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  // Access is given by those who could link the seats themselves (docs/33 §5).
  const canAccess = can('extension:write') && can('user:write');
  const needle = q.trim().toLowerCase();
  // Matched on everything shown in a row, so "sales" finds everyone in Sales and "1002" its owner.
  const hit = (u: TenantUser) => !needle || [
    u.email, u.full_name ?? '', u.role_name,
    ...u.departments.map((d) => d.name), ...u.extensions.flatMap((e) => [e.number, e.display_name ?? '']),
  ].some((v) => v.toLowerCase().includes(needle));
  const shown = users.data?.data.filter(hit) ?? [];
  const pending = users.data?.pending.filter((p) => !needle || [p.email, p.role_name, ...p.department_names,
    ...p.extension_numbers].some((v) => v.toLowerCase().includes(needle))) ?? [];

  return (
    <>
      <SectionHead title={title} sub={subtitle}
        action={can('user:invite') && !inviting && (
          <button type="button" className="arc-btn arc-btn-primary arc-btn-sm"
            onClick={() => setInviting((v) => !v)}>
            <Icon name="plus" size={13} />
            Invite user
          </button>
        )} />

      {users.error && <ErrorBanner error={users.error} />}
      <StepUpPrompt step={step} what="Resetting two-step verification" />
      {(resend.error || revoke.error) && <ErrorBanner error={resend.error ?? revoke.error} />}
      {resent && (
        <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>
            {resent.emailed ? `Invitation emailed again to ${resent.email}` : `New link for ${resent.email}`}
          </div>
          <div className="arc-subtle" style={{ fontSize: 12, marginBottom: 8 }}>
            The old link no longer works. This one works once and expires in 7 days.
          </div>
          <div className="arc-row">
            <input className="arc-input arc-mono" readOnly value={resent.link}
              onFocus={(e) => e.currentTarget.select()} aria-label="Invitation link" />
            <button type="button" className="arc-btn" onClick={() => { void navigator.clipboard?.writeText(resent.link); }}>Copy</button>
            <button type="button" className="arc-btn" onClick={() => setResent(null)}>Done</button>
          </div>
        </div>
      )}
      {inviting && <InviteForm tenantId={tenantId} onDone={() => setInviting(false)} />}

      {(users.data?.data.length ?? 0) + (users.data?.pending.length ?? 0) > 8 && (
        <div style={{ marginBottom: 10, maxWidth: 320 }}>
          <SearchInput value={q} onChange={setQ} label="Search users"
            placeholder="Name, email, role, department or extension" />
        </div>
      )}

      <div className="arc-card">
        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr><th>User</th><th>Role</th><th>Access</th><th>Status</th><th>Last seen</th><th /></tr>
            </thead>
            {users.isLoading ? <TableSkeleton cols={6} /> : (
              <tbody>
                {shown.map((u) => (
                  <Fragment key={u.id}>
                  <tr>
                    <td>
                      <div className="arc-stack">
                        <span style={{ fontWeight: 550 }}>{u.full_name ?? u.email}</span>
                        {u.full_name && <span className="arc-subtle">{u.email}</span>}
                      </div>
                    </td>
                    <td>
                      {can('membership:write') && u.id !== me
                        ? <RolePicker tenantId={tenantId} user={u} />
                        : <Badge tone="info">{u.role_name}</Badge>}
                    </td>
                    <td>
                      <div className="arc-row" style={{ gap: 6 }}>
                        <span className={accessSummary(u) ? undefined : 'arc-subtle'}>{accessSummary(u) ?? 'None'}</span>
                        {canAccess && editing !== u.id && (
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                            onClick={() => setEditing(u.id)}>
                            Edit
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <Badge tone={u.status === 'active' ? 'ok' : 'muted'}>
                        <span className="arc-dot" aria-hidden="true" />{u.status}
                      </Badge>
                    </td>
                    <td className="arc-muted">
                      {u.last_seen_at ? relativeTime(u.last_seen_at) : 'never'}
                    </td>
                    <td className="arc-td-right">
                      {can('user:write') && u.id !== me && u.mfa_enabled && (
                        <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-row-actions"
                          onClick={() => {
                            if (confirm(`Turn off two-step verification for ${u.email}? They sign in with their password alone until they set it up again.`)) {
                              step.run(() => resetMfa.mutateAsync(u.id)).catch(toastError);
                            }
                          }}>
                          Reset 2FA
                        </button>
                      )}
                      {can('user:delete') && u.id !== me && (
                        <button
                          type="button" className="arc-btn arc-btn-ghost arc-btn-sm arc-row-actions"
                          onClick={() => {
                            if (confirm(`Remove ${u.email} from this account? They lose access immediately.`)) {
                              remove.mutate(u.id);
                            }
                          }}
                        >
                          Remove
                        </button>
                      )}
                      {u.id === me && <span className="arc-subtle">you</span>}
                    </td>
                  </tr>
                  {editing === u.id && (
                    <tr>
                      <td colSpan={6} style={{ background: 'var(--arc-surface-2)' }}>
                        <AccessEditor tenantId={tenantId} user={u} onDone={() => setEditing(null)} />
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}

                {pending.map((p) => (
                  <tr key={p.id} style={{ background: 'var(--arc-surface-2)' }}>
                    <td>
                      <div className="arc-stack">
                        <span>{p.email}</span>
                        <span className="arc-subtle">{p.expired ? 'invited, the link has expired' : 'invited, not yet accepted'}</span>
                      </div>
                    </td>
                    <td><Badge tone="muted">{p.role_name}</Badge></td>
                    <td>
                      {p.extension_numbers.length + p.department_names.length > 0 ? (
                        <span className="arc-subtle">
                          {[...p.department_names, ...p.extension_numbers].join(', ')} on accept
                        </span>
                      ) : null}
                    </td>
                    <td>
                      <Badge tone={p.expired ? 'muted' : 'warn'}>
                        <span className="arc-dot" aria-hidden="true" />{p.expired ? 'expired' : 'pending'}
                      </Badge>
                    </td>
                    <td className="arc-muted">
                      {p.expired ? `expired ${relativeTime(p.expires_at)}` : `expires ${relativeTime(p.expires_at).replace(' ago', '')}`}
                    </td>
                    <td className="arc-td-right">
                      {can('user:invite') && (
                        <div className="arc-row" style={{ gap: 4, justifyContent: 'flex-end' }}>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={resend.isPending}
                            onClick={() => resend.mutate(p.id, {
                              onSuccess: (r) => setResent({
                                email: r.email, emailed: r.emailed, link: `${window.location.origin}/invite/${r.token}`,
                              }),
                            })}>
                            Resend
                          </button>
                          <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" disabled={revoke.isPending}
                            onClick={() => { if (confirm(`Revoke the invitation to ${p.email}? Its link stops working.`)) revoke.mutate(p.id); }}>
                            Revoke
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {!users.isLoading && (users.data?.data.length ?? 0) === 0 &&
         (users.data?.pending.length ?? 0) === 0 && (
          <Empty icon="users" title="No portal users"
            hint="Invite someone to let them manage this PBX." />
        )}
        {needle && shown.length === 0 && pending.length === 0 && (users.data?.data.length ?? 0) > 0 && (
          <Empty title="No match" hint="Nobody here matches that search." />
        )}
      </div>

      {can('role:read') && <RolesSection tenantId={tenantId} />}
    </>
  );
}

/**
 * A person's role, changed in place (docs/33). Offered only the roles the person changing it may
 * give; somebody who holds more than they do keeps a plain badge, as the API would refuse anyway.
 */
function RolePicker({ tenantId, user }: { tenantId: string; user: TenantUser }) {
  const roles = useGrantableRoles(tenantId);
  const change = useChangeMembershipRole(tenantId);
  const options = roles.data?.data ?? [];
  const current = options.find((r) => r.key === user.role_key);
  if (!roles.isLoading && !current) return <Badge tone="info">{user.role_name}</Badge>;
  return (
    <div className="arc-stack" style={{ gap: 2 }}>
      <select className="arc-select" style={{ minWidth: 150 }} aria-label={`Role for ${user.email}`}
        value={current?.id ?? ''} disabled={change.isPending || roles.isLoading}
        onChange={(e) => {
          const next = options.find((r) => r.id === e.target.value);
          if (next && confirm(`Change ${user.email} to ${next.name}? It takes effect immediately.`)) {
            change.mutate({ membershipId: user.membership_id, roleId: next.id });
          }
        }}>
        {roles.isLoading && <option value="">{user.role_name}</option>}
        {options.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
      {change.error && <span className="arc-error" style={{ fontSize: 11 }}>{(change.error as Error).message}</span>}
    </div>
  );
}

/**
 * Somebody's departments and own extensions (docs/33 §5), saved together. Under the TMS User role
 * these are all they see; under a broader role they are the seats that are theirs.
 */
function AccessEditor({ tenantId, user, onDone }: { tenantId: string; user: TenantUser; onDone: () => void }) {
  const save = useSetUserAccess(tenantId);
  const [access, setAccess] = useState<Access>({ departments: user.departments, extensions: user.extensions });
  return (
    <div className="arc-stack" style={{ gap: 12, padding: '6px 0' }}>
      {save.error && <ErrorBanner error={save.error} />}
      <AccessPicker tenantId={tenantId} userId={user.id} value={access} onChange={setAccess} />
      <div className="arc-row" style={{ gap: 8, justifyContent: 'flex-end' }}>
        <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
        <button type="button" className="arc-btn arc-btn-primary" disabled={save.isPending}
          onClick={() => save.mutate({
            userId: user.id,
            department_ids: access.departments.map((d) => d.id),
            extension_ids: access.extensions.map((e) => e.id),
          }, { onSuccess: onDone })}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function InviteForm({ tenantId, onDone }: { tenantId: string; onDone: () => void }) {
  const can = useSession((s) => s.can);
  const roles = useGrantableRoles(tenantId);
  const invite = useInviteTenantUser(tenantId);
  const [email, setEmail] = useState('');
  const [roleKey, setRoleKey] = useState('tenant_admin');
  const none: Access = { departments: [], extensions: [] };
  const [access, setAccess] = useState<Access>(none);
  const [link, setLink] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);
  // Given when they accept (docs/33 §5), by somebody who could give it afterwards.
  const linkable = can('extension:write') && can('user:write');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    invite.mutate({
      email, role_key: roleKey,
      department_ids: access.departments.map((d) => d.id),
      extension_ids: access.extensions.map((x) => x.id),
    }, {
      onSuccess: (r) => {
        // No mail is sent yet, so the link is surfaced here rather than
        // silently created and lost.
        setLink(`${window.location.origin}/invite/${r.token}`);
        setSentTo(r.emailed ? r.email : null);
        setEmail('');
        setAccess(none);
      },
    });
  };

  if (link) {
    return (
      <div className="arc-card arc-card-body" style={{ marginBottom: 14 }}>
        <div className="arc-banner arc-banner-info" style={{ marginBottom: 10 }}>
          <Icon name="check" size={14} />
          <div>
            <div style={{ fontWeight: 600 }}>{sentTo ? `Invitation emailed to ${sentTo}` : 'Invitation created'}</div>
            <div style={{ fontSize: 12 }}>
              {sentTo
                ? 'The link is also here, to send another way. It works once, expires in 7 days, and only they set the password.'
                : 'Send this link. It works once, expires in 7 days, and only they set the password.'}
            </div>
          </div>
        </div>
        <div className="arc-row">
          <input className="arc-input arc-mono" readOnly value={link}
            onFocus={(e) => e.currentTarget.select()} aria-label="Invitation link" />
          <button type="button" className="arc-btn"
            onClick={() => { void navigator.clipboard?.writeText(link); }}>
            Copy
          </button>
          <button type="button" className="arc-btn" onClick={() => { setLink(null); onDone(); }}>
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form className="arc-card" style={{ marginBottom: 14 }} onSubmit={submit}>
      <div className="arc-card-head">
        <h2 className="arc-h2">Invite someone</h2>
        <span className="arc-subtle">they get an email with a link to set their password</span>
      </div>
      <div className="arc-card-body">
        {invite.error && <ErrorBanner error={invite.error} />}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14 }}>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="iu-email">Email address</label>
            <input id="iu-email" type="email" className="arc-input" required value={email} autoFocus
              autoComplete="off" onChange={(e) => setEmail(e.target.value)} placeholder="person@company.co.za" />
          </div>
          <div className="arc-field" style={{ margin: 0 }}>
            <label className="arc-label" htmlFor="iu-role">Role</label>
            <select id="iu-role" className="arc-select" value={roleKey}
              onChange={(e) => setRoleKey(e.target.value)}>
              {roles.data?.data.map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
            </select>
            {/* Only roles the inviter holds themselves are offered, and the API
                refuses the rest — the list is a convenience, not the control. */}
            <div className="arc-hint">
              {roles.data?.data.find((r) => r.key === roleKey)?.description}
            </div>
          </div>
        </div>

        {linkable && (
          <div style={{ marginTop: 18 }}>
            <div className="arc-label" style={{ marginBottom: 2 }}>Access</div>
            <div className="arc-hint" style={{ margin: '0 0 10px' }}>
              What they can see, given when they accept. With the TMS User role, this is all of it.
            </div>
            <AccessPicker tenantId={tenantId} value={access} onChange={setAccess} />
          </div>
        )}
      </div>
      <div className="arc-row" style={{ gap: 8, justifyContent: 'flex-end', padding: '12px 16px',
        borderTop: '1px solid var(--arc-border)' }}>
        <button type="button" className="arc-btn" onClick={onDone}>Cancel</button>
        <button type="submit" className="arc-btn arc-btn-primary" disabled={invite.isPending || !email}>
          {invite.isPending ? 'Sending…' : 'Send invitation'}
        </button>
      </div>
    </form>
  );
}
